import api from '@utils/api'
import { Horizontal, Texto, Vertical, addCommasToNumber } from '@gravitate-js/excalibrr'
import { Spin } from 'antd'
import dayjs from 'dayjs'
import { SupplyPriceTag } from '@components/_SiteWide/OrderDisplay/SupplyPriceTag'
import { useQuery } from '@tanstack/react-query'
import type { DirectivePriceData } from '../types'
import type { AxiosResponse } from 'axios'

const columnHeaders = [
  { title: 'Terminal', flex: 2.5 },
  { title: 'Product', flex: 1.5 },
  { title: 'Loaded', flex: 1 },
  { title: 'Scheduled', flex: 1 },
  { title: 'Planned', flex: 1 },
  { title: 'Price', flex: 2 },
]

const useDirectivePriceData = (group_id: string, projected_load_date: string) => {
  return useQuery<AxiosResponse<DirectivePriceData[]>>({
    queryKey: ['directivePriceData', group_id, projected_load_date],
    queryFn: () => api.getDailyDirectivePrices({}, projected_load_date, group_id),
  })
}

export type OrderDirectivePopoverProps = {
  group_id: string
  projected_load_date: string
}

export function DirectivesTable({ group_id, projected_load_date }: OrderDirectivePopoverProps) {
  const { data: queryData } = useDirectivePriceData(group_id, projected_load_date)
  const directiveData = queryData?.data

  return (
    <>
      <Horizontal border='bottom' gap={16} className=' px-2 py-1 mt-3'>
        {columnHeaders.map((header, i) => (
          <TableHeader key={i} title={header.title} flex={header.flex} />
        ))}
      </Horizontal>
      <Spin spinning={!queryData} style={{ marginTop: queryData ? 0 : 4 }}>
        <Vertical style={{ display: 'flex', maxHeight: '25vh', minHeight: queryData ? 0 : 50, overflowY: 'auto' }}>
          {directiveData?.map((c, i) => (
            <Horizontal
              flex={1}
              verticalCenter
              border={'bottom'}
              key={i}
              gap={16}
              className='row px-2 py-1'
              style={{
                minHeight: '20px', // Enforce minimum height for rows
                flexShrink: 0, // Prevent items from shrinking
              }}
            >
              <Texto style={{ flex: columnHeaders[0].flex, lineHeight: '1em' }}>{c.terminal}</Texto>
              <Texto style={{ flex: columnHeaders[1].flex, lineHeight: '1em' }}>{c.product}</Texto>
              <Texto align='right' style={{ flex: columnHeaders[2].flex }}>
                {addCommasToNumber(c.loaded)}
              </Texto>
              <Texto align='right' style={{ flex: columnHeaders[3].flex }}>
                {addCommasToNumber(c.scheduled)}
              </Texto>
              <Texto align='right' style={{ flex: columnHeaders[4].flex }}>
                {addCommasToNumber(c.planned)}
              </Texto>
              <Vertical style={{ flex: columnHeaders[5].flex, textAlign: 'right', overflow: 'visible' }}>
                <SupplyPriceTag price={c.price} deltaToLow={c.delta_to_low ?? 0} className='mb-2 report-tag popover' />
              </Vertical>
            </Horizontal>
          ))}
        </Vertical>
      </Spin>
    </>
  )
}

function TableHeader({ title, flex = 1 }: { title: string; flex?: number }) {
  return (
    <Texto appearance='hint' style={{ whiteSpace: 'nowrap', flex, textTransform: 'uppercase' }} category='label'>
      {title}
    </Texto>
  )
}
